import React, { useEffect, useMemo } from "react";

import { getEnvironment, useGetReferrerByToken, useSearchParams, useTranslation } from "utils";

import { COUNTRY_CODE, PREVIEW_ONLY_MARKETS, SUPPORTED_MARKETS } from "consts";

import { AddressForm, AddressFormFieldName, Flex, Form, FormInstance, Input, InputMasked, InputNumber, Option, Select, Typography } from "components";

import { ConfirmAccountFormType, PartnerOverrideConfiguration } from "pages/app/account/ConfirmAccount";

type FormElements = keyof Pick<ConfirmAccountFormType, "farmName" | "country" | "addressStreet" | "addressNumber" | "addressCity" | "zip" | "phone">;
type PartnerFormOverride = Partial<Record<FormElements, PartnerOverrideConfiguration>>;

/** Note that the partner name must EXACTLY match the name of the partner in the
 * `partners` table in the DB (as looked up by the referral code)
 */
const PARTNER_FORM_OVERRIDES_STEP_2: { [partnerName: string]: PartnerFormOverride | undefined } = {};

export const ConfirmAccountStep2 = ({ formRef }: { formRef: FormInstance<ConfirmAccountFormType> }) => {
  const { t } = useTranslation(["signinup", "enums"]);
  const env = getEnvironment();
  const form = Form.useFormInstance();
  const countryCode = Form.useWatch("country", form);

  const partnerReferralToken = useSearchParams()[0].get("partner");
  const referrerName = useGetReferrerByToken(partnerReferralToken ?? "");
  const partnerFormConfiguration = PARTNER_FORM_OVERRIDES_STEP_2[referrerName.data?.name?.toLowerCase() ?? ""];

  const countryCodes = useMemo(
    () =>
      env.production ? Object.values(SUPPORTED_MARKETS).filter((countryCode) => !PREVIEW_ONLY_MARKETS.includes(countryCode)) : Object.values(SUPPORTED_MARKETS),
    [env.production]
  );

  const handleCountryChange = () => {
    form.setFieldsValue({ region: null });
  };

  useEffect(() => {
    if (partnerFormConfiguration) {
      (Object.keys(partnerFormConfiguration) as FormElements[]).forEach((formElementName) =>
        partnerFormConfiguration?.[formElementName]?.value ? formRef.setFieldValue(formElementName, partnerFormConfiguration[formElementName].value) : undefined
      );
    }
  }, [formRef, partnerFormConfiguration]);

  return (
    <Flex vertical gap={32}>
      <Flex vertical gap={8}>
        <Typography variant="h2">{t("confirm.farmDetails.title")}</Typography>
        <Typography variant="paragraph-m">{t("confirm.farmDetails.description")}</Typography>
      </Flex>
      <Input
        dataTestId="confirm-farm-name-input"
        name="farmName"
        label={partnerFormConfiguration?.farmName?.label ?? t("confirm.inputs.farmName")}
        rules={[{ required: true }]}
        initialValue={partnerFormConfiguration?.farmName?.value !== undefined ? partnerFormConfiguration.farmName.value.toString() : undefined}
        disabled={partnerFormConfiguration?.farmName?.value !== undefined}
      />
      <InputNumber
        dataTestId="confirm-total-farm-size-input"
        name="totalFarmSize"
        label={t("confirm.inputs.totalFarmSize")}
        rules={[{ required: true }]}
        precision={2}
        step={0.01}
        min={0}
        suffix="ha"
      />
      <Select
        dataTestId="confirm-country-select"
        name="country"
        label={t("confirm.inputs.selectCountry")}
        showSearch
        filterOption={(input, option) =>
          String(option?.children ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        rules={[{ required: true }]}
        onChange={handleCountryChange}
        initialValue={partnerFormConfiguration?.country?.value ?? undefined}
        disabled={partnerFormConfiguration?.country?.value !== undefined}
      >
        {countryCodes.map((countryCode, i) => (
          <Option data-testid={`confirm-country-select-option-${i}`} value={countryCode} key={countryCode}>
            {t(`enums:countries.${countryCode}`)}
          </Option>
        ))}
      </Select>
      <AddressForm
        dataTestId="confirm-account"
        address={null}
        countryCode={countryCode as COUNTRY_CODE}
        hiddenFields={
          [
            partnerFormConfiguration?.addressStreet?.value ? "addressStreet" : null,
            partnerFormConfiguration?.addressNumber?.value ? "addressNumber" : null,
            partnerFormConfiguration?.addressCity?.value ? "addressCity" : null,
          ].filter((fieldName) => fieldName !== null) as AddressFormFieldName[]
        }
      />
      {/* @ts-expect-error convert to TS */}
      <InputMasked
        className={`${partnerFormConfiguration?.phone?.hidden ? "hidden" : ""}`}
        dataTestId="confirm-phone-input"
        name="phone"
        label={t("confirm.inputs.phone")}
        rules={[{ required: true }]}
        mask="+00 00 000000[000]"
        initialValue={partnerFormConfiguration?.phone?.value}
        disabled={partnerFormConfiguration?.phone?.value !== undefined}
      />
    </Flex>
  );
};
